import React, { useEffect, useState } from 'react';
import classnames from 'classnames/bind';
import { Helmet } from 'react-helmet';
import { SwiperSlide } from 'swiper/react';
import { Autoplay, EffectCreative, Pagination } from 'swiper/modules';
import { Button } from '../../components/Button';
import { SocialMenu } from '../../components/SocialMenu';
import { Swiper } from '../../components/Swiper';
import { OnboardingCard } from '../../components/CS/OnboardingCard';
import { TournamentCard } from '../../components/CS/TournamentCard';
import { AuthSidebar } from '../../components/CS/AuthSidebar';
import { Typography } from '../../components/Typography';
import CustomLink from '../../../ui-v1/components/ui/CustomLink';
import { Logo } from '../../components/Logo';
import { YoutubePlayer } from '../../components/YoutubePlayer';
import { GameList } from '../../components/GameList';
import { useManager } from '../../shared/useManager';
import { useRouting } from '../../shared/useRouting';
import { useDocumentTitle } from '../../shared/useDocumentTitle';
import { getTournaments } from '../../../lib/use-cases/get-tournaments';
import { jsonLd } from '../../../lib/shared/json-ld';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { pageDynamicPathnames, pagePathnames } from '../page-routes';
import { COLLECT_CARDS_LINK, COMPANY_EMAIL, HELP_AND_SUPPORT_LINK, INTRO_VIDEO_LINK, SECONDARY_COMPANY_EMAIL } from '../../../ui-v1/constants/links';
import { CS_HOME_FAQ_LIST, ONBOARDING_LIST } from '../../../ui-v1/constants/cs-home';
import { ABOUT_GAME_LINKS, LEGAL_NOTES_LINKS } from '../../../ui-v1/constants/menus';
import { COPYRIGHT_INFORMATION, LEGAL_DISCLAIMER_INFORMATION } from '../../../ui-v1/constants/general';
import mainImageSrc from '../../../ui-v1/assets/images/cs-home/cs-main.png';
import placeholderForVideoImageSrc from '../../../ui-v1/assets/images/cs-placeholder-video.png';
import referersImageSrc from '../../../ui-v1/assets/images/cs-home/cs-referers.png';
import glowImageSrc from '../../../ui-v1/assets/images/cs-home/cs-glow.png';
import cardBackground1ImageSrc from '../../../ui-v1/assets/images/cs-home/cs-card-bg1.png';
import cardBackground2ImageSrc from '../../../ui-v1/assets/images/cs-home/cs-card-bg2.png';
import cardBackground3ImageSrc from '../../../ui-v1/assets/images/cs-home/cs-card-bg3.png';
import cardBackground4ImageSrc from '../../../ui-v1/assets/images/cs-home/cs-card-bg4.png';
import styles from './CounterStrikeHomePage.css';

const cardBackgroundImageUrls = [
    cardBackground1ImageSrc,
    cardBackground2ImageSrc,
    cardBackground3ImageSrc,
    cardBackground4ImageSrc
];

const TOURNAMENTS_LIMIT = 4;

const cx = classnames.bind(styles);

function CounterStrikeHomePage() {
    const [tournaments, setTournaments] = useState([]);
    const manager = useManager();
    const {navigate, location, parseLocationSearch} = useRouting();
    const [isOpenedSidebar, setIsOpenedSidebar] = useState(Boolean(parseLocationSearch(location.search).auth) || false);

    const getData = async () => {
        const list = await getTournaments({limit: TOURNAMENTS_LIMIT}, true);

        setTournaments(list);
    };

    useEffect(() => {
        logEvent(EVENT_TYPES.CS_HOMEPAGE_VISITED);
        getData();
    }, []);

    useDocumentTitle('Powerplay Counter-Strike');

    const handleClickButton = type => () => {
        logEvent(EVENT_TYPES.CLICKED_BUTTON_CS_HOMEPAGE, {type});

        if (manager.managerData?.isAuthenticated) {
            return navigate(pagePathnames.dashboard);
        }

        setIsOpenedSidebar(true);
    };
    const handleClickTournamentCard = tournamentId => {
        logEvent(EVENT_TYPES.CLICKED_BUTTON_CS_HOMEPAGE, {type: 'tournaments section'});

        if (manager.managerData?.isAuthenticated) {
            return navigate(pageDynamicPathnames.getTournament(tournamentId));
        }

        setIsOpenedSidebar(true);
    };

    const renderMainButton = () => {
        if (manager.isLoading) {
            return <div style={{height: 40}} />;
        }

        if (manager.managerData.isAuthenticated) {
            return (
                <Button
                    label='Back to the game'
                    iconLeft={{name: 'lightningBold'}}
                    className={styles.sectionGridButton}
                    link={{
                        to: pagePathnames.dashboard
                    }}
                />
            )
        }

        return (
            <Button
                label='Play now'
                onClick={handleClickButton('welcome section')}
                className={cx('sectionGridButton', 'buttonPlay')}
                iconLeft={{name: 'lightningBold'}}
            />
        )
    };

    return (
        <div className={styles.root}>
            <AuthSidebar
                isOpen={isOpenedSidebar}
                onClose={() => setIsOpenedSidebar(false)}
            />
            <header className={styles.headerWrap}>
                <div className={styles.header}>
                    <Logo url='/' className={styles.logo} />
                    {!manager.isLoading && !manager.managerData.isAuthenticated && (
                        <Button
                            label='Sign in'
                            onClick={handleClickButton('header')}
                            type='secondary'
                        />
                    )}
                </div>
            </header>
            <main className={styles.main}>
                <section className={cx('section', 'sectionGrid')}>
                    <div className={styles.sectionInfo}>
                        <h1 className={styles.title}>
                            Powerplay — <br />free-to-play manager game for Counter-Strike 2™
                        </h1>
                        <ul className={styles.list}>
                            <li className={styles.listItem}>
                                <span className={styles.listItemNumber}>1.</span>
                                <p>
                                    <span className={styles.textWithAnimation1}>Open bundles</span>
                                    {' and '}
                                    <span className={styles.textWithAnimation2}>get cards</span>
                                    {' with real players.'}
                                </p>
                            </li>
                            <li className={styles.listItem}>
                                <span className={styles.listItemNumber}>2.</span>
                                <p>
                                    <span className={styles.textWithAnimation3}>Select the tournament.</span>
                                </p>
                            </li>
                            <li className={styles.listItem}>
                                <span className={styles.listItemNumber}>3.</span>
                                <p>
                                    <span className={styles.textWithAnimation4}>Build a lineup</span>
                                    {' to '}
                                    <span className={styles.textWithAnimation5}>join the fantasy competition.</span>
                                </p>
                            </li>
                            <li className={styles.listItem}>
                                <span className={styles.listItemNumber}>4.</span>
                                <p>
                                    <span className={styles.textWithAnimation6}>Win</span>
                                    {' cards and other valuable '}
                                    <span className={styles.textWithAnimation7}>in-game assets</span>
                                    {' to make your '}
                                    <span className={styles.textWithAnimation8}>team stronger.</span>
                                </p>
                            </li>
                        </ul>
                        {renderMainButton()}
                    </div>
                    <p className={styles.sectionCopyrightInfo}>
                        Counter-Strike and the Counter-Strike logo are trademarks and/or registered trademarks of the Valve Corporation.
                    </p>
                    <img
                        src={`${mainImageSrc}?v=3`}
                        className={styles.sectionImage}
                        alt='statistics'
                    />
                    <div className={styles.glowBlock} />
                </section>
                <section className={styles.section}>
                    <div className={styles.sectionInfo}>
                        <h2 className={styles.secondaryTitle}>
                            Latest tournaments
                        </h2>
                        <div className={styles.tournamentList}>
                            {tournaments.map((tournament, index) => (
                                <TournamentCard
                                    key={tournament.id}
                                    date={tournament.date}
                                    name={tournament.name}
                                    onClick={() => handleClickTournamentCard(tournament.id)}
                                    imageUrl={tournament.imageUrl}
                                    backgroundImageUrl={cardBackgroundImageUrls[index]}
                                    registrationState={tournament.registrationState}
                                    description={tournament.description}
                                />
                            ))}
                        </div>
                    </div>
                </section>
                <section className={cx('section', 'sectionColumnInOnboarding')}>
                    <div className={styles.onboardingBlock}>
                        <h2 className={styles.onboardingBlockTitle}>It's easy to get started</h2>
                        <Swiper
                            className={styles.onboardingBlockSlider}
                            speed={1000}
                            modules={[Autoplay, Pagination, EffectCreative]}
                            slidesPerGroup={1}
                            slidesPerView={1}
                            autoplay={{
                                delay: 6000
                            }}
                            pagination={{
                                type: 'bullets',
                                horizontalClass: styles.onboardingBlockPagination,
                                bulletActiveClass: styles.bulletActive,
                                bulletClass: styles.bullet,
                                clickable: true
                            }}
                            simulateTouch={false}
                            rewind
                            grabCursor={false}
                            effect='creative'
                            creativeEffect={{
                                prev: {
                                    translate: [0, 0, -600],
                                    opacity: 0
                                },
                                next: {
                                    translate: ['100%', 0, 0],
                                    opacity: 1
                                }
                            }}
                        >
                            {ONBOARDING_LIST.map(item => (
                                <SwiperSlide key={item.id}>
                                    <OnboardingCard
                                        {...item}
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </section>
                <section className={cx('section', 'sectionFlex')}>
                    <div className={styles.sectionInfo}>
                        <h2 className={styles.secondaryTitle}>
                            Would you like to know more?
                        </h2>
                        <p className={styles.sectionLabel}>
                            Watch this video to learn how to play on the Powerplay platform and get started now.
                        </p>
                        <CustomLink
                            target='_blank'
                            to={HELP_AND_SUPPORT_LINK}
                            className={cx('sectionFlexLink', 'sectionLink')}
                            withDefaultClassName={false}
                        >
                            Get help and answers
                        </CustomLink>
                    </div>
                    <div className={styles.sectionVideoWrap}>
                        <img
                            src={glowImageSrc}
                            className={styles.videoGlow}
                            alt='glow'
                            loading='lazy'
                        />
                        <YoutubePlayer
                            placeholderImageUrl={placeholderForVideoImageSrc}
                            url={INTRO_VIDEO_LINK}
                            className={styles.player}
                        />
                    </div>
                </section>
                <section className={cx('section', 'sectionFlex')}>
                    <div className={styles.sectionInfo}>
                        <h2 className={styles.secondaryTitle}>
                            How can I collect more cards?
                        </h2>
                        <CustomLink
                            target='_blank'
                            to={COLLECT_CARDS_LINK}
                            className={cx('sectionFlexLink', 'sectionLink')}
                            withDefaultClassName={false}
                        >
                            Know more
                        </CustomLink>
                    </div>
                    <div className={styles.sectionImageWrap}>
                        <img
                            src={referersImageSrc}
                            className={styles.sectionImage}
                            alt='referer'
                            loading='lazy'
                        />
                        <img
                            src={glowImageSrc}
                            className={styles.refererPrimaryGlow}
                            alt='glow'
                            loading='lazy'
                        />
                        <img
                            src={glowImageSrc}
                            className={styles.refererSecondaryGlow}
                            alt='glow'
                            loading='lazy'
                        />
                    </div>
                </section>
                <section className={cx('section', 'sectionColumn')}>
                    <div className={styles.sectionInfo}>
                        <h2 className={cx('secondaryTitle', 'secondaryTitleWithCenter')}>
                            Find out more
                        </h2>
                        <Typography
                            className={styles.description}
                            variant='body-2-regular'
                        >
                            We focus on giving our users the opportunity to play as many fantasy games as possible. Stay tuned for more updates! 
                        </Typography>
                        <GameList landingType='cs' />                   
                    </div>
                </section>
                <section className={cx('section', 'sectionColumn')}>
                    <div className={styles.sectionInfo}>
                        <h2 className={cx('secondaryTitle', 'secondaryTitleWithCenter')}>
                            Frequently asked questions
                        </h2>
                        <div className={styles.masonryList}>
                            {CS_HOME_FAQ_LIST.map(({id, answer, question}) => (
                                <div
                                    key={id}
                                    className={cx('masonryCard', 'questionAnswerBlock')}
                                >
                                    <p className={styles.masonryCardLabel}>{question}</p>
                                    <p className={styles.masonryCardValue}>{answer}</p>
                                </div>
                            ))}
                            <div className={styles.masonryCard}>
                                <p className={styles.masonryCardLabel}>For more information, click here</p>
                                <CustomLink
                                    target='_blank'
                                    to={HELP_AND_SUPPORT_LINK}
                                    className={cx('masonryCardLink', 'sectionLink')}
                                >
                                    Help and support
                                </CustomLink>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <footer className={styles.footerWrapper}>
                <img
                    src={glowImageSrc}
                    className={styles.footerGlow}
                    alt='glow'
                    loading='lazy'
                />
                <div className={styles.footer}>
                    <div className={styles.footerBlock}>
                        <Logo url='/' className={styles.logo} />
                        <p className={cx('footerLabel', 'footerCopyright')}>
                            {COPYRIGHT_INFORMATION}
                        </p>
                        <Typography
                            className={styles.legalDisclaimerText}
                            variant='badge-1'
                        >
                            {LEGAL_DISCLAIMER_INFORMATION}
                        </Typography>
                        <a
                            href={`mailto:${COMPANY_EMAIL}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={styles.footerLabel}
                        >
                            {COMPANY_EMAIL}
                        </a>
                        <a
                            href={`mailto:${SECONDARY_COMPANY_EMAIL}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={styles.footerLabel}
                        >
                            {SECONDARY_COMPANY_EMAIL}
                        </a>
                    </div>
                    <div className={styles.footerBlock}>
                        <p className={styles.footerSubTitle}>About the game</p>
                        {ABOUT_GAME_LINKS.map(item => (
                            <CustomLink
                                key={item.title}
                                to={item.link}
                                target={item.target}
                                className={styles.footerMenuItem}
                            >
                                {item.title}
                            </CustomLink>
                        ))}
                    </div>
                    <div className={styles.footerBlock}>
                        <p className={styles.footerSubTitle}>Legal notes</p>
                        {LEGAL_NOTES_LINKS.map(item => (
                            <CustomLink
                                key={item.title}
                                to={item.link}
                                target={item.target}
                                className={`${styles.footerMenuItem}${item.className ? ' ' + item.className : ''}`}
                            >
                                {item.title}
                            </CustomLink>
                        ))}
                    </div>
                    <div className={styles.footerBlock}>
                        <p className={styles.footerSubTitle}>Get in touch</p>
                        <p className={cx('footerLabel', 'footerLabelWithIndent')}>Become a part of Powerplay family!</p>
                        <SocialMenu withBorder />
                    </div>
                </div>
            </footer>
            <Helmet>
                <link rel='canonical' href={`${window.location.origin}${pagePathnames.counterStrikeHome}`} />
                <script type='application/ld+json'>
                    {jsonLd.generateFAQPage(CS_HOME_FAQ_LIST)}
                </script>
            </Helmet>
        </div>
    );
}

export {
    CounterStrikeHomePage
};
